import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Translator } from './translator';

const GOOGLE_CLIENT_ID = window.GOOGLE_CLIENT_ID || "933936399610-77rkvhdrmks3urktevj6lasah45235t8.apps.googleusercontent.com";
const BASE = "https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com";

const baseTextByLang = {
  it: {
    text1: "Iscriviti Gratuitamente",
    text2: "Iscriviti per goderti il patrimonio culturale!",
    text3:
      "Iscriviti per interagire con i nostri tour digitali: potrai mettere “Mi piace”, commentare e vivere un’esperienza completa. È gratuito e veloce!",
    text4: "ISCRIVITI CON EMAIL",
    text5: "O",
    text6: "ISCRIVITI CON FACEBOOK",
    text7: "ISCRIVITI CON GOOGLE",
    text8: "Continuando, accetti i termini di Te.C.H-e",
    text9: "Condizioni d'uso",
    text10: "",
    text11: "e Informativa sulla privacy",
    text12: "Hai già un account su Te.C.H-e?",
    text13: "Accedi",
    text14: "",
    textGoogleError: "Errore durante il login con Google",
    textFacebookSDK: "SDK di Facebook non caricato",
    textFacebookCancel: "Accesso a Facebook annullato",
    textFacebookError: "Errore durante il login con Facebook",
  },

  en: {
    text1: "Sign up for free",
    text2: "Sign up to enjoy cultural heritage!",
    text3:
      "Create your account to interact with our digital tours: you can like, comment and enjoy the full experience. It’s free and quick!",
    text4: "SIGN UP WITH EMAIL",
    text5: "OR",
    text6: "SIGN UP WITH FACEBOOK",
    text7: "SIGN UP WITH GOOGLE",
    text8: "By continuing, you agree to Te.C.H-e’s",
    text9: "Terms of Use",
    text10: "and",
    text11: "Privacy Policy",
    text12: "Already have a Te.C.H-e account?",
    text13: "Log in",
    text14: "",
    textGoogleError: "Error while signing in with Google",
    textFacebookSDK: "Facebook SDK not loaded",
    textFacebookCancel: "Facebook login cancelled",
    textFacebookError: "Error while signing in with Facebook",
  },

  fr: {
    text1: "Inscrivez-vous gratuitement",
    text2:
      "Inscrivez-vous pour profiter du patrimoine culturel !",
    text3:
      "Créez votre compte pour interagir avec nos visites numériques : vous pourrez aimer, commenter et vivre une expérience complète. C’est gratuit et rapide !",
    text4: "S’INSCRIRE AVEC L’E-MAIL",
    text5: "OU",
    text6: "S’INSCRIRE AVEC FACEBOOK",
    text7: "S’INSCRIRE AVEC GOOGLE",
    text8: "En continuant, vous acceptez les",
    text9: "Conditions d’utilisation",
    text10: "et la",
    text11: "Politique de confidentialité",
    text12: "Vous avez déjà un compte Te.C.H-e ?",
    text13: "Se connecter",
    text14: "",
    textGoogleError: "Erreur lors de la connexion avec Google",
    textFacebookSDK: "SDK Facebook non chargé",
    textFacebookCancel: "Connexion Facebook annulée",
    textFacebookError: "Erreur lors de la connexion avec Facebook",
  },
};

const SignupModal = ({ onClose }) => {
  const navigate = useNavigate();
  const { translateText, lang } = Translator();
  const modalRef = useRef(null);
  const firstFocusableRef = useRef(null);
  const googleBtnRef = useRef(null);
  const [fbReady, setFbReady] = useState(!!window.FB);

  const [isClosing, setIsClosing] = useState(false);

  // state for current language strings
  const [t, setT] = useState(baseTextByLang[lang] || baseTextByLang.en);

  // i18n: fixed IT/EN/FR, Translator for other languages
  useEffect(() => {
    // For it / en / fr → use fixed text
    if (lang === "it" || lang === "en" || lang === "fr") {
      setT(baseTextByLang[lang] || baseTextByLang.en);
      return;
    }

    // For any other lang → translate from Italian base
    let cancelled = false;
    const translateAll = async () => {
      try {
        const source = baseTextByLang.it;
        const entries = Object.entries(source);
        const translatedEntries = await Promise.all(
          entries.map(async ([key, value]) => {
            const out = await translateText(value);
            return [key, out || value];
          })
        );
        if (!cancelled) {
          setT(Object.fromEntries(translatedEntries));
        }
      } catch {
        if (!cancelled) {
          setT(baseTextByLang.en);
        }
      }
    };

    translateAll();
    return () => {
      cancelled = true;
    };
  }, [lang, translateText]);

  const closeModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        closeModal();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  useEffect(() => {
    firstFocusableRef.current?.focus();
  }, []);

  // Check for FB SDK readiness
  useEffect(() => {
    const tInt = setInterval(() => {
      if (window.FB) {
        setFbReady(true);
        clearInterval(tInt);
      }
    }, 200);
    return () => clearInterval(tInt);
  }, []);

  // init Google button once the script is available
  useEffect(() => {
    if (!window.google) return;

    async function handleGoogle(response) {
      try {
        const id_token = response.credential;
        const res = await fetch(`${BASE}/auth_google`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id_token }),
        });
        const text = await res.text();
        const data = (() => {
          try {
            return JSON.parse(text);
          } catch {
            return { status: "error", error: text || "non-JSON" };
          }
        })();

        console.log("auth/google status", res.status, "body:", data);

        if (data?.status === "ok" && data?.user) {
          localStorage.setItem("user", JSON.stringify(data.user));
          window.dispatchEvent(
            new CustomEvent("auth:login", { detail: data.user })
          );
          onClose();
        } else {
          alert(data?.error || "Google sign-in failed");
        }
      } catch (e) {
        alert(t.textGoogleError || "Errore durante il login con Google");
      }
    }

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: (response) => {
        handleGoogle(response);
      },
    });

    if (googleBtnRef.current) {
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        type: "standard",
        theme: "filled_blue",
        size: "large",
        text: "signup_with",
        shape: "pill",
        logo_alignment: "center",
        width: 360,
      });
    }
  }, [t.textGoogleError, onClose]);

  // --- Facebook --------------------------------------------------
  const handleFacebookLogin = () => {
    if (!window.FB) {
      alert(t.textFacebookSDK || "Facebook SDK not loaded");
      return;
    }

    window.FB.login(
      (resp) => {
        (async () => {
          console.log("[FB] login response", resp);
          try {
            if (!resp?.authResponse?.accessToken) {
              alert(
                t.textFacebookCancel || "Facebook login cancelled"
              );
              return;
            }
            const access_token = resp.authResponse.accessToken;

            const res = await fetch(`${BASE}/auth_facebook`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ access_token }),
            });
            const data = await res.json();

            if (data?.status === "ok" && data?.user) {
              localStorage.setItem("user", JSON.stringify(data.user));
              window.dispatchEvent(
                new CustomEvent("auth:login", { detail: data.user })
              );
              onClose();
            } else {
              alert(data?.error || "Facebook sign-in failed");
            }
          } catch (e) {
            alert(
              t.textFacebookError ||
                "Errore durante il login con Facebook"
            );
          }
        })();
      },
      { scope: "public_profile,email", return_scopes: true }
    );
  };

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 ${
        isClosing ? "animate-fadeOut" : "animate-fadeIn"
      }`}
    >
      <div
        ref={modalRef}
        className="bg-white rounded-xl max-w-md w-full p-6 text-center relative max-h-[90vh] overflow-y-auto mx-4 shadow-xl transition-all duration-300 ease-in-out"
      >
        <button
          ref={firstFocusableRef}
          onClick={closeModal}
          className="absolute top-3 right-3 text-gray-700 hover:text-black text-xl leading-none w-8 h-8 flex items-center justify-center rounded-full bg-white shadow-md"
          aria-label="Close modal"
        >
          ×
        </button>

        <h2 className="text-2xl font-bold mb-2">{t.text1}</h2>
        <p className="mb-3 text-black">{t.text2}</p>
        <p className="mb-4 text-black">{t.text3}</p>

        <button
          className="h-11 w-full max-w-[360px] mx-auto rounded-full bg-[#1877F2] text-white 
                    font-medium flex items-center justify-center gap-3 shadow-sm 
                    hover:brightness-95 transition"
          onClick={() => navigate(`/register`)}
        >
          {t.text4}
        </button>

        <div className="my-2 text-black">{t.text5}</div>

        <button
          onClick={handleFacebookLogin}
          disabled={!fbReady}
          aria-disabled={!fbReady}
          title={fbReady ? "" : t.textFacebookSDK}
          className={`h-11 w-full max-w-[360px] mx-auto rounded-full bg-[#1877F2] text-white
             font-medium flex items-center justify-center gap-3 shadow-sm hover:brightness-95
             transition ${
               !fbReady
                 ? "opacity-60 cursor-not-allowed hover:brightness-100"
                 : ""
             }`}
        >
          <div className="bg-white rounded-full w-10 h-10 flex items-center justify-center">
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/5/51/Facebook_f_logo_%282019%29.svg"
              className="w-5 h-5"
              alt="Facebook"
            />
          </div>
          {t.text6}
        </button>

        <div ref={googleBtnRef} className="w-full flex justify-center my-2" />

        <p className="mt-2 text-sm">
          {t.text8}{" "}
          <a
            href="/conditions"
            className="link text-blue-600 underline"
          >
            {t.text9}
          </a>{" "}
          {t.text10 && `${t.text10} `} 
          <a
            href="/privacy"
            className="link text-blue-600 underline"
          >
            {t.text11}
          </a>
          .
        </p>

        <p className="mt-2 text-sm">
          {t.text12}{" "}
          <a
            href="/login"
            className="link text-blue-600 underline"
          >
            {t.text13}
          </a>
        </p>
      </div>
    </div>
  );
};

export default SignupModal;
